import { BondFieldRenderer } from "@/components/BondFieldRenderer";
import { BondPairedField } from "@/components/BondPairedField";
import type { BondFilesPort } from "@/components/BondFilesField";
import { ReadOnlyField, ReadOnlyNumber } from "@/components/ReadOnlyField";
import { FIELD_HELP } from "@/components/fieldStyles";
import { visibleBondFields } from "@/lib/bondFieldSections";
import { activeBondPairMode, resolveBondPairs } from "@/lib/bondFieldPairs";
import {
  fieldKind,
  takesFullRow,
  toRenderFinalField,
  type BondFieldValue,
  type BondRenderField,
  type BondValueBag,
} from "@/lib/bondFields";
import { formatBondValue } from "@/lib/bondValueDisplay";
import type { BondFinalField } from "@/lib/brokerBonds";

// BRK-S418 — the bond details: the configuration's `finalStep.fields` drawn as
// controls. ONE renderer for every surface that edits them — the wizard's
// project & bond terms step, and the post-submit Edit Bond Request and Request
// Rider dialogs — so a field gets the same control everywhere, and a later
// change to how one is drawn happens once, here (Aaron, 2026-10-24).
//
// It owns the GRID and nothing about the step around it: the heading, the
// extraction banner and the missing-answers checklist stay with each host.
// Every control decision stays where it already was — `BondFieldRenderer` maps
// a declared type onto a control (D-10), `BondPairedField` draws the bond
// penalty's % / $ composite, and `visibleBondFields` drops what a
// "not applicable" answer switched off.
//
// The two props the dialogs add both default to nothing, so the wizard renders
// exactly what it did before the extraction:
//   • `locks` — field key → why it can't change now. A locked field renders as
//     its VALUE in a read-only box with the reason under it, never as a
//     disabled input a broker keeps trying to type into.
//   • `markers` — field key → "pending" (a rider awaiting approval; `onBond`
//     supplies what the bond carries today) or "updated".

export type BondFieldMarker = "pending" | "updated";

/**
 * A `files` field shown READ-ONLY (BRK-S490): after submit a bond request's
 * documents are the Submission Documents tab's to add, so the dialogs list
 * what is on the order by name and send the broker to that tab — never the
 * pre-submit "start or open the bond request first" alert.
 */
export interface BondFilesReadOnly {
  /** The order's documents, by name; null while they are read. */
  names: readonly string[] | null;
  /** Opens the Submission Documents tab. */
  onOpenDocuments: () => void;
}

export function BondDetailsFields({
  fields,
  values,
  onChange,
  invalid,
  pendingReview = () => false,
  filesPort,
  locks,
  markers,
  onBond,
  filesReadOnly,
}: {
  /** The configuration's final-step fields, as declared. */
  fields: readonly BondFinalField[];
  values: BondValueBag;
  onChange: (name: string, value: BondFieldValue | undefined) => void;
  /** Field name → why its value isn't an answer, client-side or server-side
   *  alike (BRK-S091) — rendered inline on the offending control. */
  invalid: Record<string, string>;
  /** Whether this field came from an uploaded document and still awaits the
   *  broker's confirmation (BRK-S120). */
  pendingReview?: (name: string) => boolean;
  /** What a `files` field needs (BRK-S145). */
  filesPort?: BondFilesPort;
  /** Field key → the reason it is read-only. Absent: everything is editable. */
  locks?: Readonly<Record<string, string>>;
  /** Field key → its change marker. Absent: no markers. */
  markers?: Readonly<Record<string, BondFieldMarker>>;
  /** The values the bond carries today, for a pending marker's
   *  "Currently on bond" line. */
  onBond?: BondValueBag;
  /** Draw `files` fields read-only (the post-submit dialogs, BRK-S490). */
  filesReadOnly?: BondFilesReadOnly;
}) {
  const rendered = fields.map(toRenderFinalField);
  const lockOf = (names: readonly string[]) =>
    names.map((name) => locks?.[name]).find((reason) => reason !== undefined);
  const markerFor = (names: readonly string[]) => {
    for (const name of names) {
      const marker = markers?.[name];
      const field = rendered.find((f) => f.name === name);
      if (marker !== undefined && field !== undefined) {
        return (
          <ChangeMarker marker={marker} field={field} onBond={onBond?.[name]} />
        );
      }
    }
    return null;
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {/* BRK-S169 — a DECLARED pair collapses into one control with a unit
          toggle; everything else renders exactly as it did. The table is
          consulted here and in `BondPairedField`, never inside the generic
          renderer, which may not know a field's name. */}
      {resolveBondPairs(
        // BRK-S168 — a unit answered "not applicable" takes its length and
        // its figure off the step. Narrowed BEFORE the pairing, so a
        // suppressed member can never form half a composite.
        visibleBondFields(rendered, values),
      ).map((unit) => {
        if (unit.kind === "pair") {
          const names = unit.fields.map((field) => field.name);
          const reason = lockOf(names);
          if (reason !== undefined) {
            const mode = activeBondPairMode(unit, values);
            const active =
              unit.fields.find((field) => field.name === mode.key) ??
              unit.fields[0];
            return (
              <div key={unit.id} data-field={active.name}>
                <LockedField
                  field={{ ...active, label: unit.pair.label }}
                  value={values[active.name]}
                  reason={reason}
                />
              </div>
            );
          }
          return (
            <div key={unit.id}>
              <BondPairedField
                unit={unit}
                values={values}
                onChange={onChange}
                invalid={invalid}
                pendingReview={pendingReview}
              />
              {markerFor(names)}
            </div>
          );
        }
        const rf = unit.field;
        const reason = lockOf([rf.name]);
        return (
          <div
            key={unit.id}
            // BRK-S319 — where an error summary's link sends focus.
            data-field={rf.name}
            className={takesFullRow(rf) ? "sm:col-span-2" : undefined}
          >
            {rf.type === "files" && filesReadOnly !== undefined ? (
              <ReadOnlyDocuments label={rf.label} files={filesReadOnly} />
            ) : reason !== undefined ? (
              <LockedField field={rf} value={values[rf.name]} reason={reason} />
            ) : (
              <>
                <BondFieldRenderer
                  field={rf}
                  value={values[rf.name]}
                  onChange={(v) => onChange(rf.name, v)}
                  error={invalid[rf.name]}
                  pendingReview={pendingReview(rf.name)}
                  files={filesPort}
                />
                {markerFor([rf.name])}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A field that can't change now: its value as the record shows it (D-10 —
 * `m/d/yyyy`, grouped money; the wire is untouched) and the reason it is
 * locked. Text, not a disabled input, so nothing reads as a box to type into
 * (the BRK-S349 read-only rule).
 */
function LockedField({
  field,
  value,
  reason,
}: {
  field: BondRenderField;
  value: BondFieldValue | undefined;
  reason: string;
}) {
  const kind = fieldKind(field);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dl>
        {kind === "currency" || kind === "number" || kind === "percent" ? (
          <ReadOnlyNumber
            kind={kind}
            label={field.label}
            value={typeof value === "number" ? value : undefined}
          />
        ) : (
          <ReadOnlyField
            label={field.label}
            value={formatBondValue(value, {
              type: field.type,
              options: field.options,
            })}
          />
        )}
      </dl>
      <p className={FIELD_HELP}>{reason}</p>
    </div>
  );
}

/** A rider's pending change, or a change already applied to the bond — the
 *  one copy of the marker, shared with the submission page's read view
 *  (BRK-S415). `onBond` is what the bond carries today, as captured. */
export function ChangeMarker({
  marker,
  field,
  onBond,
}: {
  marker: BondFieldMarker;
  field: Pick<BondRenderField, "type" | "options">;
  onBond: unknown;
}) {
  if (marker === "updated") {
    return <p className="mt-1 text-xs font-medium text-brk-accent">Updated</p>;
  }
  const current = formatBondValue(onBond, {
    type: field.type,
    options: field.options,
  });
  return (
    <div className="mt-1 flex flex-col gap-0.5 text-xs">
      <p className="font-medium text-amber-800 dark:text-amber-200">
        Pending approval
      </p>
      {current !== null && (
        <p className="text-brk-dim">Currently on bond: {current}</p>
      )}
    </div>
  );
}

/** A `files` field after submit (BRK-S490): the order's documents by name and
 *  the way to the tab that adds them. Text, not a drop zone. */
function ReadOnlyDocuments({
  label,
  files,
}: {
  label: string;
  files: BondFilesReadOnly;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5" data-files-read-only>
      <p className="text-sm font-medium text-brk-text">{label}</p>
      {files.names === null ? (
        <p className="text-sm text-brk-dim">Loading documents…</p>
      ) : files.names.length === 0 ? (
        <p className="text-sm text-brk-dim">
          No documents on this request yet.
        </p>
      ) : (
        <ul className="flex list-disc flex-col gap-0.5 pl-5 text-sm text-brk-text">
          {files.names.map((name) => (
            <li key={name} className="[overflow-wrap:anywhere]">
              {name}
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={files.onOpenDocuments}
        className="self-start text-sm font-medium text-brk-accent hover:underline"
      >
        Add documents on the Submission Documents tab ›
      </button>
    </div>
  );
}
